import React, { useCallback, useEffect, useState } from "react";
import { Modal, List, Avatar, Tag, Typography, DatePicker, Spin } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { getUnmarkedAttendanceListApi } from "../../../apiservice/hrpayroll";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Distinguishes the three cron-generated states (see attendanceReport.js) —
// same three literal `notes` markers the backend query filters on.
const STATUS_LABEL = {
  "Marked absent automatically": { label: "Auto-marked absent", color: "orange" },
  "Auto-marked: pending regularization": { label: "Pending regularization", color: "gold" },
  "Auto-marked: attendance not recorded": { label: "Deducted from leave", color: "red" },
};

export default function UnmarkedAttendanceModal({ open, onClose, employees }) {
  const authInfo = useSelector(selectAuthInfo);
  const [range, setRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const employeeMap = React.useMemo(() => {
    const map = {};
    (employees || []).forEach((emp) => {
      map[emp.id] = emp.user_name;
    });
    return map;
  }, [employees]);

  const load = useCallback(async () => {
    if (!range?.[0] || !range?.[1]) return;
    setLoading(true);
    try {
      const res = await getUnmarkedAttendanceListApi({
        branch_id: authInfo?.userInfo?.branch_id,
        from_date: range[0].format("YYYY-MM-DD"),
        to_date: range[1].format("YYYY-MM-DD"),
      });
      setData(res.data?.unmarked || []);
    } catch (err) {
      console.error("Failed to load unmarked attendance:", err);
    } finally {
      setLoading(false);
    }
  }, [range, authInfo?.userInfo?.branch_id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Modal
      title="Unmarked Attendance"
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      styles={{
        body: {
          maxHeight: "60vh",
          overflowY: "auto",
        },
      }}
    >
      <RangePicker
        value={range}
        onChange={(v) => setRange(v)}
        style={{ marginBottom: 16, width: "100%" }}
        allowClear={false}
      />

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Spin />
        </div>
      ) : data.length ? (
        <List
          dataSource={data}
          renderItem={(item) => {
            const status = STATUS_LABEL[item.notes] || { label: item.notes, color: "default" };
            return (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<ExclamationCircleOutlined />} />}
                  title={<Text strong>{employeeMap[item.emp_id] || item.emp_id}</Text>}
                  description={
                    <>
                      <div>Date: {dayjs(item.attendance_date).format("DD-MM-YYYY")}</div>
                      {item.pending_deduction_date && (
                        <div>
                          Deducts on: {dayjs(item.pending_deduction_date).format("DD-MM-YYYY")}
                        </div>
                      )}
                      <div>
                        Status: <Tag color={status.color}>{status.label}</Tag>
                      </div>
                    </>
                  }
                />
              </List.Item>
            );
          }}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Text type="secondary">No unmarked attendance in this range 🎉</Text>
        </div>
      )}
    </Modal>
  );
}
